import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { useTierFeatures } from '@/hooks/useTierFeatures'
import { getPricingLink } from '@/config/domains'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Displays the user's current tier as a small badge.
 * Links to /pricing for free users, /admin/billing for paid users.
 */
export const TierBadge = () => {
  const { tier, tierDisplayName, isLoading } = useTierFeatures()

  if (isLoading) {
    return <Skeleton className="w-12 h-6 rounded-full" />
  }

  // Determine badge styling based on tier
  const getBadgeStyles = () => {
    switch (tier) {
      case 'free':
        return 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      case 'pro':
        return 'bg-framed-yellow-100 text-framed-yellow-700 hover:bg-framed-yellow-200'
      case 'agency':
        return 'bg-amber-100 text-amber-700 hover:bg-amber-200'
      default:
        return 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }
  }

  // Tooltip text based on tier
  const getTooltipText = () => {
    switch (tier) {
      case 'free':
        return 'You\'re on the Start plan. Click to upgrade.'
      case 'pro':
        return 'You\'re on the Pro plan. Click to manage billing.'
      case 'agency':
        return 'You\'re on the Agency plan. Click to manage billing.'
      default:
        return 'Click to view plan details.'
    }
  }

  const badgeClassName = cn(
    'inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full',
    'text-xs font-medium transition-colors',
    getBadgeStyles()
  )

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {tier === 'free' ? (
            <a href={getPricingLink()} className={badgeClassName}>
              {tierDisplayName}
              <ArrowUpRight className="w-3 h-3" />
            </a>
          ) : (
            <Link to="/tokens" className={badgeClassName}>
              {tierDisplayName}
            </Link>
          )}
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">{getTooltipText()}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
